import classNames from 'classnames';
import React, { ThHTMLAttributes } from 'react';
import { Stack } from '../stack';
import { TableSortIndicator, TableSortIndicatorProps } from '../table/TableSortIndicator';
import { ColumnAlign } from '../table/types';
import { TABLE_HEAD_LABEL_GAP } from './constants';
import { StyledTableCol } from './styles';

export interface TableColProps extends ThHTMLAttributes<HTMLTableCellElement> {
  id?: string;
  width?: number;
  fixed?: boolean;
  fixedColWidth?: number;
  stickyHeader?: boolean;
  groupColumn?: boolean;
  sortable?: boolean;
  resizable?: boolean;
  align?: ColumnAlign;
  /**
   * 테이블 컬럼의 수직 정렬 방향
   *
   * - middle: 세로 가운데 정렬 (기본값)
   * - top: 상단 정렬
   * - bottom: 하단 정렬
   * - baseline: 텍스트 기준선 정렬
   *
   * @default middle
   */
  verticalAlign?: 'middle' | 'top' | 'bottom' | 'baseline';
  /**
   * 테이블 정렬 인디케이터 Props
   *
   * sortable이 true일 때 TableSortIndicator 컴포넌트에 전달되는 props입니다.
   * isSorted, direction 등의 정렬 상태 정보를 포함합니다.
   *
   * @default undefined
   */
  sortIndicatorProps?: TableSortIndicatorProps;
  hasRightBorder?: boolean;
  /**
   * rowSpan 으로 해당 영역(thead/tbody)의 마지막 행까지 이어지는 셀에 지정하세요.
   * @default false
   */
  lastRow?: boolean;
  onClick?: () => void;
}

export const TableCol = React.forwardRef<HTMLTableCellElement, TableColProps>(
  (
    {
      children,
      className,
      onClick,
      verticalAlign = 'middle',
      sortIndicatorProps,
      width,
      id,
      hasRightBorder,
      lastRow,
      resizable: resizableProp,
      ...props
    }: TableColProps,
    ref,
  ) => {
    if (props.fixed && !width) throw new Error('fixed 테이블 컬럼은 `width` 속성이 존재해야합니다.');
    const resizable = resizableProp && !width;

    return (
      <StyledTableCol
        scope='col'
        className={classNames(
          id ? `pds-table-cell-${id}` : 'pds-table-cell',
          props.fixed && 'pds-table-cell-fixed',
          hasRightBorder && 'show-right-border',
          lastRow && 'pds-table-col-last-row',
          className,
        )}
        resizable={resizable}
        {...props}
        onClick={() => onClick?.()}
        ref={ref}
        valign={verticalAlign}
      >
        <Stack
          direction='row'
          align='center'
          gap={TABLE_HEAD_LABEL_GAP}
          style={{
            justifyContent:
              props.align === 'center'
                ? 'center'
                : props.align === 'right'
                ? 'flex-end'
                : props.sortable
                ? 'space-between'
                : 'initial',
          }}
        >
          {resizable ? <span className='ContentsWrapper'>{children}</span> : children}
          {props.sortable && (
            <TableSortIndicator
              {...sortIndicatorProps}
              isSorted={Boolean(sortIndicatorProps?.isSorted)}
              sortDirection={sortIndicatorProps?.sortDirection ?? 'default'}
            />
          )}
        </Stack>
      </StyledTableCol>
    );
  },
);
